export type Medcall2OptionRow = {
  option_name: string | null;
  option_value_json: string | null;
};

type IntakeOption = {
  label: string;
  value: string;
};

type IntakeProgramOption = IntakeOption & {
  requiresClinicalContacts: boolean;
  requiresFacility: boolean;
};

const readOptionList = (rows: Medcall2OptionRow[], name: string): unknown[] => {
  // Legacy uses the first matching row and the stored array order.
  const json = rows.find((row) => row.option_name === name)?.option_value_json;
  if (!json) {
    return [];
  }

  try {
    const value: unknown = JSON.parse(json);
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const isOptionRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

export const readMedcall2ProviderOptions = (rows: Medcall2OptionRow[] = []): IntakeOption[] => (
  readOptionList(rows, 'providers').flatMap((provider) => (
    typeof provider === 'string' ? [{ label: provider, value: provider }] : []
  ))
);

export const readMedcall2IntakeOptions = (rows: Medcall2OptionRow[] = []) => {
  const programOptions: IntakeProgramOption[] = readOptionList(rows, 'programs').flatMap((option) => {
    if (!isOptionRecord(option) || typeof option.name !== 'string' || typeof option.value !== 'string') {
      return [];
    }

    // AddParticipant gates these fields by search key, not the displayed label.
    const requiresClinicalContacts = !['zodr-scattered', 'fist-erc', 'dsh-erc'].includes(option.name);
    return [{
      label: option.value,
      requiresClinicalContacts,
      requiresFacility: requiresClinicalContacts,
      value: option.name,
    }];
  });
  const facilityOptions: IntakeOption[] = readOptionList(rows, 'facilities').flatMap((option) => {
    if (!isOptionRecord(option) || typeof option.name !== 'string') {
      return [];
    }

    if (typeof option.option_facility_id !== 'number' && typeof option.option_facility_id !== 'string') {
      return [];
    }

    return [{ label: option.name, value: String(option.option_facility_id) }];
  });
  const filterDomains = readOptionList(rows, 'filter_domains').filter((domain): domain is string => (
    typeof domain === 'string'
  ));

  return { facilityOptions, filterDomains, programOptions };
};
